import React from 'react';
import { getOrderedTypes, type CelebrationTypeEntry } from '@/config/celebrationRegistry';

interface TypeGalleryPickerProps {
  selectedType: string | null;
  onSelect: (typeKey: string) => void;
}

const TIER_LABELS: Record<number, string> = {
  1: 'Popular',
  2: 'Cultural & Life',
  3: 'Community',
};

export const TypeGalleryPicker: React.FC<TypeGalleryPickerProps> = ({ selectedType, onSelect }) => {
  const orderedTypes = getOrderedTypes();

  // Group by tier
  const tiers = orderedTypes.reduce<Record<number, CelebrationTypeEntry[]>>((acc, t) => {
    if (!acc[t.tier]) acc[t.tier] = [];
    acc[t.tier].push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {[1, 2, 3].map((tier) => {
        const types = tiers[tier];
        if (!types || types.length === 0) return null;
        return (
          <div key={tier}>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              {TIER_LABELS[tier]}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {types.map((type) => {
                const isSelected = selectedType === type.key;
                return (
                  <button
                    key={type.key}
                    type="button"
                    onClick={() => onSelect(type.key)}
                    className={`
                      group relative rounded-xl border overflow-hidden text-left
                      transition-all duration-200 hover:shadow-md hover:-translate-y-0.5
                      ${isSelected
                        ? 'ring-2 ring-primary border-primary shadow-md'
                        : 'hover:border-primary/40'
                      }
                    `}
                  >
                    <div className={`bg-gradient-to-br ${type.gradient} p-4 flex flex-col items-center text-center`}>
                      {/* Large emoji */}
                      <div className="text-4xl mb-2 group-hover:scale-110 transition-transform duration-200">
                        {type.emoji}
                      </div>
                      {/* SVG watermark */}
                      <img
                        src={type.svgPath}
                        alt=""
                        className="absolute top-2 right-2 w-10 h-10 opacity-10 pointer-events-none"
                        aria-hidden
                      />
                      {/* Label */}
                      <h4 className="font-semibold text-sm">{type.label}</h4>
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">
                        {type.tagline}
                      </p>
                    </div>
                    {/* Selected checkmark */}
                    {isSelected && (
                      <div className="absolute top-2 left-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
